export type AiFlowchartShapeId =
  | 'process'
  | 'decision'
  | 'start-end'
  | 'document'
  | 'database'
  | 'manual'
  | 'input-output'
  | 'predefined'
  | 'display'
  | 'storage'
  | 'internal'
  | 'off-page-connector'
  | 'on-page-connector'
  | 'junction'
  | 'merge'
  | 'or'
  | 'sort'
  | 'collate'
  | 'extract'
  | 'delay'
  | 'loop-limit'
  | 'preparation'
  | 'multi-doc'
  | 'manual-operation';

export interface AiFlowchartShapeDef {
  id: AiFlowchartShapeId;
  width: number;
  height: number;
  strokeWidth: number;
  paths: string[];
  fillPaths?: string[];
  // Default text label to apply on creation (when applicable)
  text?: string;
}

// These shapes previously had no default text; defaults are omitted intentionally.
// Shapes not listed below provide a default label via the `text` field.

export const AI_FLOWCHART_SHAPES: Record<AiFlowchartShapeId, AiFlowchartShapeDef> = {
  process: {
    id: 'process',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Process',
    paths: [
      'M116.852459 0.959986H2.754148C1.667685 0.959986 0.786933 2.034504 0.786933 3.359988V56.16C0.786933 57.48552 1.667685 58.56 2.754148 58.56H116.852459C117.939344 58.56 118.819672 57.48552 118.819672 56.16V3.359988C118.819672 2.034504 117.939344 0.959986 116.852459 0.959986Z',
    ],
  },
  decision: {
    id: 'decision',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Decision',
    paths: [
      'M58.906359 0.978561C59.483897 0.706114 60.16359 0.706114 60.741128 0.978561L117.692308 27.846871C119.205128 28.560194 119.205128 30.595935 117.692308 31.309161L60.741128 58.177548C60.16359 58.449968 59.483897 58.449968 58.906359 58.177548L1.954697 31.309161C0.442804 30.595935 0.442807 28.560194 1.954697 27.846871L58.906359 0.978561Z',
    ],
  },
  'start-end': {
    id: 'start-end',
    width: 120,
    height:40,
    strokeWidth: 1.6,
    text: 'Start/End',
    paths: [
      'M101.114754 0.842093H18.491803C8.713692 0.842093 0.786933 9.325116 0.786933 19.789474C0.786933 30.253789 8.713692 38.736842 18.491803 38.736842H101.114754C110.892787 38.736842 118.819672 30.253789 118.819672 19.789474C118.819672 9.325116 110.892787 0.842093 101.114754 0.842093Z',
    ],
  },
  document: {
    id: 'document',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Document',
    paths: [
      'M0.786933 3.000054C0.786933 1.816586 1.667685 0.857195 2.754148 0.857195H116.852459C117.939344 0.857195 118.819672 1.816586 118.819672 3.000054V50.984571C118.819672 52.450714 117.447541 53.466107 116.164918 53.023607C60.201443 33.729107 58.920689 75.278679 2.051361 50.6745C1.307833 50.352857 0.786933 49.550786 0.786933 48.679393V3.000054Z',
    ],
  },
  database: {
    id: 'database',
    width: 120,
    height: 80,
    strokeWidth: 1.6,
    text: 'Database',
    fillPaths: [
      'M1.116007 16.432432C1.116007 7.834757 27.35414 0.864918 59.720651 0.864918C92.087163 0.864918 118.325302 7.834757 118.325302 16.432432V63.135243C118.325302 71.732973 92.087163 78.702703 59.720651 78.702703C27.35414 78.702703 1.116007 71.732973 1.116007 63.135243V16.432432Z',
    ],
    paths: [
      'M118.325302 16.432432C118.325302 25.03027 92.087163 32 59.720651 32C27.35414 32 1.116007 25.03027 1.116007 16.432432M118.325302 16.432432C118.325302 7.834757 92.087163 0.864918 59.720651 0.864918C27.35414 0.864918 1.116007 7.834757 1.116007 16.432432M118.325302 16.432432V63.135243M1.116007 16.432432V63.135243M1.116007 63.135243C1.116007 71.732973 27.35414 78.702703 59.720651 78.702703C92.087163 78.702703 118.325302 71.732973 118.325302 63.135243M1.116007 63.135243C1.116007 54.537514 27.35414 47.567676 59.720651 47.567676C92.087163 47.567676 118.325302 54.537514 118.325302 63.135243',
    ],
  },
  manual: {
    id: 'manual',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Manual',
    paths: [
      'M0.786693 24.24072C0.786693 23.05284 1.499095 22.04352 2.462085 21.86736L116.560328 0.987328C117.749508 0.76967 118.819672 1.893492 118.819672 3.36078V55.77396C118.819672 57.09936 117.938361 58.17396 116.852459 58.17396H2.753902C1.667439 58.17396 0.786693 57.09936 0.786693 55.77396V24.24072Z',
    ],
  },
  'input-output': {
    id: 'input-output',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Input Output',
    paths: [
      'M11.762975 2.777904C11.983736 1.709496 12.777124 0.959986 13.68724 0.959986H116.705455C117.995702 0.959986 118.94281 2.427324 118.629421 3.942072L107.720331 56.74212C107.500165 57.81048 106.706777 58.56 105.796364 58.56H2.778149C1.487762 58.56 0.540933 57.09264 0.853898 55.57788L11.762975 2.777904Z',
    ],
  },
  predefined: {
    id: 'predefined',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Predefined',
    paths: [
      'M12.590164 0.960059V58.56M107.016393 0.960059V58.56M2.754148 0.960059H116.852459C117.939344 0.960059 118.819672 2.034576 118.819672 3.36006V56.16C118.819672 57.48552 117.939344 58.56 116.852459 58.56H2.754148C1.667685 58.56 0.786933 57.48552 0.786933 56.16V3.36006C0.786933 2.034576 1.667685 0.960059 2.754148 0.960059Z',
    ],
  },
  display: {
    id: 'display',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Display',
    paths: [
      'M3.644995 2.781348C3.867218 1.709076 4.718888 0.960059 5.69348 0.960059H111.041416C111.84531 0.960059 112.584425 1.47444 112.930619 2.294988C120.657345 20.60508 120.657345 38.91504 112.930619 57.22512C112.584425 58.04568 111.84531 58.56012 111.041416 58.56012H5.69348C4.718888 58.56012 3.867218 57.81108 3.644995 56.73876C-0.08253 38.75292 -0.08253 20.7672 3.644995 2.781348Z',
    ],
  },
  storage: {
    id: 'storage',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Storage',
    fillPaths: [
      'M18.491803 0.774241H101.114754C110.892787 0.774241 118.819672 13.772419 118.819672 29.806548C118.819672 45.840581 110.892787 58.83871 101.114754 58.83871H18.491803C8.713692 58.83871 0.786933 45.840581 0.786933 29.806548C0.786933 13.772419 8.713692 0.774241 18.491803 0.774241Z',
    ],
    paths: [
      'M18.491803 58.838806C28.270033 58.838806 36.196721 45.840581 36.196721 29.806548C36.196721 13.772419 28.270033 0.774241 18.491803 0.774241M18.491803 58.838806C8.713692 58.838806 0.786933 45.840581 0.786933 29.806548C0.786933 13.772419 8.713692 0.774241 18.491803 0.774241M18.491803 58.838806L101.114754 58.83871M18.491803 0.774241H101.114754M101.114754 0.774241C91.336623 0.774241 83.409836 13.772419 83.409836 29.806548C83.409836 45.840581 91.336623 58.83871 101.114754 58.83871M101.114754 0.774241C110.892787 0.774241 118.819672 13.772419 118.819672 29.806548C118.819672 45.840581 110.892787 58.83871 101.114754 58.83871',
    ],
  },
  internal: {
    id: 'internal',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Internal',
    paths: [
      'M0.786933 22.56H118.819672M18.491803 0.960059V58.56M2.754148 0.960059H116.852459C117.939344 0.960059 118.819672 2.034576 118.819672 3.36006V56.16C118.819672 57.48552 117.939344 58.56 116.852459 58.56H2.754148C1.667685 58.56 0.786933 57.48552 0.786933 56.16V3.36006C0.786933 2.034576 1.667685 0.960059 2.754148 0.960059Z',
    ],
  },
  'off-page-connector': {
    id: 'off-page-connector',
    width: 100,
    height: 60,
    strokeWidth: 1.6,
    text: 'Off Page Connector',
    paths: [
      'M0.816128 2.709726C0.816128 1.640787 1.729837 0.774241 2.856939 0.774241H96.73449C97.861633 0.774241 98.775306 1.640787 98.775306 2.709726V40.078065C98.775306 40.884871 98.247653 41.607097 97.451122 41.890355L50.512347 58.583903C50.050306 58.748226 49.541122 58.748226 49.079184 58.583903L2.140367 41.890355C1.343827 41.607097 0.816128 40.884871 0.816128 40.078065V2.709726Z',
    ],
  },
  'on-page-connector': {
    id: 'on-page-connector',
    width: 60,
    height: 60,
    strokeWidth: 1.6,
    paths: [
      'M29.806258 58.838806C45.84029 58.838806 58.838516 45.840581 58.838516 29.806548C58.838516 13.772419 45.84029 0.774241 29.806258 0.774241C13.772226 0.774241 0.774005 13.772419 0.774005 29.806548C0.774005 45.840581 13.772226 58.838806 29.806258 58.838806Z',
    ],
  },
  junction: {
    id: 'junction',
    width: 60,
    height: 60,
    strokeWidth: 1.6,
    paths: [
      'M6.580452 29.806548H53.032065M29.806258 6.580694V53.032258M58.838516 29.806548C58.838516 45.840581 45.84029 58.838806 29.806258 58.838806C13.772226 58.838806 0.774005 45.840581 0.774005 29.806548C0.774005 13.772419 13.772226 0.774241 29.806258 0.774241C45.84029 0.774241 58.838516 13.772419 58.838516 29.806548Z',
    ],
  },
  merge: {
    id: 'merge',
    width: 100,
    height: 60,
    strokeWidth: 1.6,
    paths: [
      'M48.233333 1.604967C49.088817 0.628573 50.592473 0.628573 51.447957 1.604967L98.271935 55.048145C99.504731 56.4552 98.520215 58.679564 96.664624 58.679564H3.016667C1.161075 58.679564 0.176543 56.4552 1.409333 55.048145L48.233333 1.604967Z',
    ],
  },
  or: {
    id: 'or',
    width: 60,
    height: 60,
    strokeWidth: 1.6,
    paths: [
      'M12.387097 47.225806L47.225806 12.387194M12.387097 12.387194L47.225806 47.225806M58.838806 29.806548C58.838806 45.840581 45.840581 58.838806 29.806548 58.838806C13.772419 58.838806 0.774241 45.840581 0.774241 29.806548C0.774241 13.772419 13.772419 0.774241 29.806548 0.774241C45.840581 0.774241 58.838806 13.772419 58.838806 29.806548Z',
    ],
  },
  sort: {
    id: 'sort',
    width: 90,
    height: 60,
    strokeWidth: 1.6,
    fillPaths: [
      'M43.680066 1.412476C44.42578 0.692703 45.539308 0.692703 46.285121 1.412476L88.493242 42.149673C89.867769 43.476327 89.017121 45.973527 87.190714 45.973527H2.774453C0.948019 45.973527 0.097386 43.476327 1.471915 42.149673L43.680066 1.412476Z',
      'M46.285121 57.807273C45.539308 58.527055 44.42578 58.527055 43.680066 57.807273L1.471915 17.07C0.097386 15.743455 0.948019 13.246255 2.774453 13.246255H87.190714C89.017121 13.246255 89.867769 15.743455 88.493242 17.07L46.285121 57.807273Z',
    ],
    paths: [
      'M43.680066 1.412476C44.42578 0.692703 45.539308 0.692703 46.285121 1.412476L88.493242 42.149673C89.867769 43.476327 89.017121 45.973527 87.190714 45.973527H2.774453C0.948019 45.973527 0.097386 43.476327 1.471915 42.149673L43.680066 1.412476Z',
      'M46.285121 57.807273C45.539308 58.527055 44.42578 58.527055 43.680066 57.807273L1.471915 17.07C0.097386 15.743455 0.948019 13.246255 2.774453 13.246255H87.190714C89.017121 13.246255 89.867769 15.743455 88.493242 17.07L46.285121 57.807273Z',
    ],
  },
  collate: {
    id: 'collate',
    width: 60,
    height: 50,
    strokeWidth: 1.6,
    paths: [
      'M1.565409 4.46415C0.045589 3.3665 0.76287 0.800049 2.589471 0.800049H56.856185C58.682769 0.800049 59.4 3.3665 57.880246 4.46415L1.565409 45.1359C0.045589 46.2336 0.76287 48.8001 2.589471 48.8001H56.856185C58.682769 48.8001 59.4 46.2336 57.880246 45.136L1.565409 4.46415Z',
    ],
  },
  extract: {
    id: 'extract',
    width: 100,
    height: 60,
    strokeWidth: 1.6,
    paths: [
      'M19.7812 39.861536H79.7812M49.7812 1.290107L97.7812 59.14725H1.78125L49.7812 1.290107Z',
    ],
  },
  delay: {
    id: 'delay',
    width: 120,
    height: 60,
    strokeWidth: 1.2,
    text: 'Delay',
    paths: ['M0.600098 0.599976H90.6001C110.6 16.6 110.6 32.6 90.6001 48.6H0.600098V0.599976Z'],
  },
  'loop-limit': {
    id: 'loop-limit',
    width: 100,
    height: 50,
    strokeWidth: 1.6,
    paths: [
      'M18.865979 24.8H80.721649M19.896907 48.8H79.690722C80.339794 48.8 80.950825 48.5037 81.340309 48L98.350619 26C98.900412 25.2889 98.900412 24.3112 98.350619 23.6L81.340309 1.60005C80.950825 1.09644 80.339794 0.800049 79.690825 0.800049H19.896907C19.247938 0.800049 18.636907 1.09644 18.247423 1.60005L1.237165 23.6C0.687335 24.3112 0.687336 25.2889 1.237165 26L18.247526 48C18.636907 48.5037 19.247938 48.8 19.896907 48.8Z',
    ],
  },
  preparation: {
    id: 'preparation',
    width: 100,
    height: 50,
    strokeWidth: 1.6,
    text: 'Preparation',
    paths: [
      'M1.237165 26C0.687336 25.2889 0.687335 24.3112 1.237165 23.6L18.247423 1.60005C18.636907 1.09644 19.247938 0.800049 19.896907 0.800049H79.690722C80.339794 0.800049 80.950825 1.09644 81.340206 1.60005L98.350515 23.6C98.900412 24.3112 98.900412 25.2889 98.350515 26L81.340206 48C80.950825 48.5037 80.339794 48.8 79.690722 48.8H19.896907C19.247938 48.8 18.636907 48.5037 18.247423 48L1.237165 26Z',
    ],
  },
  'multi-doc': {
    id: 'multi-doc',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Multi Doc',
    fillPaths: [
      'M79.418182 53.236473C87.869127 59.997164 96.320073 60.2112 104.770909 53.878691C105.301309 53.481273 105.6 52.850618 105.6 52.187782V16.145564C105.6 14.940545 104.6232 13.963745 103.418291 13.963745H3.0546C1.849615 13.963745 0.872781 14.940545 0.872781 16.145564V48.696873C0.872781 50.526327 3.026738 51.577964 4.615255 50.670436C12.095018 46.397236 19.574836 47.252618 27.054545 53.236473C35.781818 60.218291 44.509091 60.218291 53.236364 53.236473C61.963636 46.254655 70.690909 46.254655 79.418182 53.236473Z',
    ],
    paths: [
      'M7.418236 7.418236H109.963636C111.169091 7.418236 112.145455 8.395069 112.145455 9.600055V46.691018M13.963636 0.872781H116.509091C117.714545 0.872781 118.690909 1.849615 118.690909 3.0546V40.145455M79.418182 53.236473C87.869127 59.997164 96.320073 60.2112 104.770909 53.878691C105.301309 53.481273 105.6 52.850618 105.6 52.187782V16.145564C105.6 14.940545 104.6232 13.963745 103.418291 13.963745H3.0546C1.849615 13.963745 0.872781 14.940545 0.872781 16.145564V48.696873C0.872781 50.526327 3.026738 51.577964 4.615255 50.670436C12.095018 46.397236 19.574836 47.252618 27.054545 53.236473C35.781818 60.218291 44.509091 60.218291 53.236364 53.236473C61.963636 46.254655 70.690909 46.254655 79.418182 53.236473Z',
    ],
  },
  'manual-operation': {
    id: 'manual-operation',
    width: 120,
    height: 60,
    strokeWidth: 1.6,
    text: 'Manual Operation',
    paths: [
      'M0.853898 3.942144C0.540932 2.427396 1.487762 0.960059 2.778149 0.960059H116.705455C117.995702 0.960059 118.94281 2.427396 118.629421 3.942144L107.720331 56.74212C107.500165 57.8106 106.706777 58.56012 105.796364 58.56012H13.68724C12.777124 58.56012 11.983736 57.8106 11.762975 56.74212L0.853898 3.942144Z',
    ],
  },
};

export const AI_FLOWCHART_SHAPE_IDS: AiFlowchartShapeId[] = [
  'process',
  'decision',
  'start-end',
  'document',
  'database',
  'manual',
  'input-output',
  'predefined',
  'display',
  'storage',
  'internal',
  'off-page-connector',
  'on-page-connector',
  'junction',
  'merge',
  'or',
  'sort',
  'collate',
  'extract',
  'delay',
  'loop-limit',
  'preparation',
  'multi-doc',
  'manual-operation',
];
